// Dynamic Pie Chart Component - Handles any data structure

import React, { useMemo, useState, useEffect } from 'react';
import { PieChart as PieChartIcon } from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import CustomTooltip from '../common/CustomTooltip';
import { COLORS, CHART_COLORS } from '../../constants/theme';

// Hook to track window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Build count-by-category data for pie slices when no numeric data exists
const buildPieCountData = (rows) => {
  const keys = Object.keys(rows[0]);
  const PREFERRED = ['status', 'type', 'market', 'country', 'priority', 'category', 'region'];
  const candidates = keys.filter(k => {
    if (k === 'id') return false;
    const uniques = new Set(rows.map(r => r[k]).filter(v => v != null && String(v).trim() !== '')).size;
    return uniques >= 2 && uniques <= Math.max(rows.length, 10);
  });
  candidates.sort((a, b) => {
    const ai = PREFERRED.findIndex(p => a.toLowerCase().includes(p));
    const bi = PREFERRED.findIndex(p => b.toLowerCase().includes(p));
    if (ai !== -1 && bi === -1) return -1;
    if (bi !== -1 && ai === -1) return 1;
    return new Set(rows.map(r => r[a])).size - new Set(rows.map(r => r[b])).size;
  });
  const groupKey = candidates[0] || keys[0];
  const counts = {};
  rows.forEach(row => {
    const val = String(row[groupKey] ?? 'Unknown').trim() || 'Unknown';
    counts[val] = (counts[val] || 0) + 1;
  });
  return { groupKey, slices: Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8) };
};

const PieChart = ({ data, isDonut = false }) => {
  // Dynamically transform data for pie chart
  const { chartData, countLabel } = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return { chartData: [], countLabel: null };

    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') return { chartData: [], countLabel: null };

    const keys = Object.keys(firstRow);
    if (keys.length === 0) return { chartData: [], countLabel: null };

    // Strategy 1: pre-formatted name/value pairs
    if (keys.includes('name') && keys.includes('value')) {
      const slices = data.map((row, idx) => ({
        name: String(row.name || `Item ${idx + 1}`),
        value: typeof row.value === 'number' ? row.value : Number(row.value) || 0,
        color: row.color || CHART_COLORS[idx % CHART_COLORS.length],
        fullName: String(row.name || `Item ${idx + 1}`),
      })).filter(i => i.value > 0).sort((a, b) => b.value - a.value).slice(0, 8);
      return { chartData: slices, countLabel: null };
    }

    // Strategy 2: first string = name, first numeric = value
    const nameKey = keys.find(key => {
      const v = firstRow[key];
      return v !== null && v !== undefined && typeof v === 'string' && String(v).length < 50;
    }) || keys[0];

    const valueKey = keys.find(key => {
      if (key === nameKey) return false;
      const v = firstRow[key];
      if (v === null || v === undefined) return false;
      return typeof v === 'number' || (typeof v === 'string' && !isNaN(Number(v)) && v.trim() !== '');
    });

    if (valueKey) {
      const slices = data.map((row, idx) => {
        const name = String(row[nameKey] || `Item ${idx + 1}`);
        const raw = row[valueKey];
        const value = Math.abs(typeof raw === 'number' ? raw : Number(raw) || 0);
        return { name: name.length > 30 ? name.substring(0, 30) + '...' : name, value, color: CHART_COLORS[idx % CHART_COLORS.length], fullName: name };
      }).filter(i => i.value > 0).sort((a, b) => b.value - a.value).slice(0, 8);

      // If all values were zero → fall through to count chart
      if (slices.length > 0) return { chartData: slices, countLabel: null };
    }

    // Strategy 3: No usable numeric column → count-by-category
    const { groupKey, slices } = buildPieCountData(data);
    const countSlices = slices.map(([label, count], idx) => ({
      name: label.length > 30 ? label.substring(0, 30) + '...' : label,
      value: count,
      color: CHART_COLORS[idx % CHART_COLORS.length],
      fullName: label,
    }));
    return { chartData: countSlices, countLabel: groupKey };
  }, [data]);

  // Calculate total for percentages
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No data available for pie chart visualization.</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No valid data available for pie chart.</p>
      </div>
    );
  }

  const [activeIndex, setActiveIndex] = useState(null);
  const { width: windowWidth } = useWindowSize();
  
  // Responsive values - improved readability
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  // Increased heights for better readability
  const chartHeight = isMobile ? 320 : isTablet ? 360 : 400;
  // Larger font sizes for readability
  const fontSize = isMobile ? 13 : isTablet ? 14 : 15;
  const innerRadius = isDonut ? (isMobile ? 50 : isTablet ? 55 : 65) : 0;
  
  // Calculate outerRadius reactively based on activeIndex
  const outerRadius = useMemo(() => {
    if (activeIndex !== null) {
      return isMobile ? 110 : isTablet ? 125 : 140;
    }
    return isMobile ? 100 : isTablet ? 115 : 130;
  }, [activeIndex, isMobile, isTablet]);

  const handlePieClick = (data, index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 animate-fade-in-down">
          <PieChartIcon className="w-5 h-5 sm:w-6 sm:h-6 animate-float flex-shrink-0" style={{ color: COLORS.primary }} />
          <span className="truncate">
            {countLabel
              ? `Distribution by ${countLabel}`
              : `${isDonut ? 'Donut Chart' : 'Pie Chart'} - Data Distribution`}
          </span>
        </h3>
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <p className="text-slate-500">Total</p>
          <p className="font-semibold text-slate-900">
            {total >= 1000000 ? `${(total / 1000000).toFixed(2)}M` : total.toLocaleString()}
          </p>
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm chart-animate card-hover">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 items-start md:items-center">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <RechartsPie>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                // Keep labels inside slices to avoid edge clipping; full names are shown in list panel
                label={({ percent }) => {
                  if (!percent || percent < 0.08) return ''; // Hide tiny slices
                  return `${(percent * 100).toFixed(0)}%`;
                }}
                labelStyle={{ fontSize: isMobile ? 10 : isTablet ? 11 : 12, fontWeight: 700, fill: '#ffffff' }}
                outerRadius={outerRadius}
                innerRadius={innerRadius}
                dataKey="value"
                stroke="#fff"
                strokeWidth={activeIndex !== null ? (isMobile ? 3 : 4) : (isMobile ? 2 : 3)}
                activeIndex={activeIndex}
                activeShape={{
                  outerRadius: outerRadius + 10,
                  stroke: COLORS.primary,
                  strokeWidth: 2,
                }}
                onClick={handlePieClick}
                style={{ cursor: 'pointer' }}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                    opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
                    style={{ 
                      filter: activeIndex === index ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip 
                content={<CustomTooltip />}
                animationDuration={300}
                animationEasing="ease-out"
                formatter={(value, name, props) => {
                  const percent = total > 0 ? ((value / total) * 100).toFixed(2) : '0';
                  const formattedValue = value >= 1000000 
                    ? `${(value / 1000000).toFixed(2)}M`
                    : value >= 1000 
                    ? `${(value / 1000).toFixed(2)}K`
                    : value.toLocaleString();
                  return [`${formattedValue} (${percent}%)`, props.payload.fullName || name];
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: `${fontSize}px`, fontWeight: '600', paddingTop: '15px' }}
                iconType="circle"
                iconSize={isMobile ? 12 : isTablet ? 14 : 16}
              />
            </RechartsPie>
          </ResponsiveContainer>
          <div className="space-y-2 sm:space-y-3 max-h-96 overflow-y-auto">
            {chartData.map((item, idx) => {
              const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
              const isActive = activeIndex === idx;
              return (
                <div 
                  key={idx} 
                  onClick={() => setActiveIndex(isActive ? null : idx)}
                  className={`flex items-center justify-between p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer touch-manipulation ${
                    isActive 
                      ? 'bg-blue-50 border-blue-300 shadow-md' 
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <div 
                      className={`w-4 h-4 sm:w-5 sm:h-5 rounded shadow-sm flex-shrink-0 transition-transform ${
                        isActive ? 'scale-125' : ''
                      }`}
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className={`text-xs sm:text-sm font-bold truncate ${
                      isActive ? 'text-blue-900' : 'text-gray-700'
                    }`} title={item.fullName || item.name}>
                      {item.name}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className={`text-base sm:text-lg font-bold ${
                      isActive ? 'text-blue-900' : 'text-gray-900'
                    }`}>{item.value.toLocaleString()}</span>
                    <span className="text-xs text-gray-500 ml-1">({percent}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PieChart;
